import { AutoCompleteProps, useAutoComplete } from '../hooks/use-smarty-streets';
import { cn } from '@/shadcn-ui/utils';
import { Command, CommandInput, CommandItem, CommandList } from '@/shadcn-ui/command';
import { getStyledSuggestion } from '../utils/smarty-streets';
import { useSpinDelay } from 'spin-delay';
import { useState, useRef, useEffect, useCallback } from 'react';
import Spinner from '@/icons/spinner';
import type { AddressWithComponents } from '../types/smarty-streets';
import GuidePopover from '@/common/components/guide-popover';
import { isEmpty } from 'lodash-es';

interface SmartyStreetsInputProps extends Omit<AutoCompleteProps, 'inputValue'> {
  name: string;
  placeholder?: string;
  defaultValue?: string;
  /**
   * If set to true, the input will display only the street line (address line 1)
   * when a user selects a suggestion. Defaults to false, displaying the full address.
   */
  limitToStreetLine?: boolean;
  errorText?: string;
  handleSuggestionSelectDelegate?: (AddressWithComponents: AddressWithComponents) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Input component that uses the SmartyStreets API to provide address suggestions,
 * styled to match the existing input component in our UI library.
 */
export default function SmartyStreetsInput({
  name,
  placeholder,
  defaultValue,
  limitToStreetLine = false,
  errorText,
  smartyStreetsEmbeddedKey,
  onSuggestions,
  handleSuggestionSelectDelegate,
  onChange,
}: SmartyStreetsInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState(defaultValue ?? '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { suggestions, loading } = useAutoComplete({
    inputValue: value,
    smartyStreetsEmbeddedKey,
    onSuggestions,
  });

  const shouldShowLoadingSpinner = useSpinDelay(loading);
  const shouldShowSuggestions =
    showSuggestions && suggestions && suggestions.length > 0 && defaultValue !== value;

  function handleBlur(e: React.FocusEvent<HTMLDivElement>) {
    if (!containerRef?.current?.contains(e.relatedTarget)) {
      setShowSuggestions(false);
    }
  }

  function handleValueChange(input: string) {
    setValue(input);
    setShowSuggestions(true);

    if (onChange) {
      const syntheticEvent = {
        target: {
          value: input,
          name: name,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  }

  function handleFocus() {
    setShowSuggestions(true);
  }

  const handleSuggestionSelect = useCallback(
    (suggestion: AddressWithComponents) => {
      const selectedValue = limitToStreetLine
        ? suggestion.components.streetLine
        : suggestion.address;
      setValue(selectedValue);
      handleSuggestionSelectDelegate?.(suggestion);
      setShowSuggestions(false);
    },
    [handleSuggestionSelectDelegate, limitToStreetLine]
  );

  useEffect(() => {
    if (value === defaultValue && suggestions?.length && suggestions?.length > 0) {
      handleSuggestionSelect(suggestions[0]);
    }
  }, [defaultValue, handleSuggestionSelect, suggestions, value]);

  return (
    <GuidePopover
      show={errorText !== undefined && isEmpty(value)}
      className="w-auto"
      triggerComponent={
        <Command
          className="fs-exclude relative h-auto overflow-visible"
          ref={containerRef}
          onBlur={handleBlur}
        >
          <CommandInput
            name={name}
            value={value}
            // defaultValue={defaultValue}
            placeholder={placeholder}
            hideContainerStyles={true}
            // Note: These styles are designed match our Input component
            className={cn(
              'disabled:bg-neutral-90 body flex h-auto items-center rounded-2xl border-2 border-flexcar-neutral80 px-4 py-3 text-base placeholder-flexcar-neutral50 shadow-inputShadow outline-none transition-colors focus:border-flexcar-blue40 focus:shadow-none disabled:cursor-not-allowed disabled:text-flexcar-neutral50 disabled:placeholder-flexcar-neutral70'
            )}
            hideIcon={true}
            tabIndex={0}
            onFocus={handleFocus}
            onValueChange={handleValueChange}
          />

          {shouldShowLoadingSpinner ? (
            <Spinner width={24} height={24} className="absolute right-3 top-2" />
          ) : null}

          <CommandList
            className={cn(
              'fs-mask invisible absolute -inset-x-0 top-14 z-50 rounded-2xl border bg-popover shadow-md',
              shouldShowSuggestions
                ? 'visible fade-in-5'
                : 'invisible animate-out fade-out-5'
            )}
          >
            {shouldShowSuggestions
              ? suggestions.map((suggestion, index) => {
                  return (
                    <CommandItem
                      key={index}
                      value={suggestion.address}
                      className={cn(
                        'fs-mask cursor-pointer px-[12px] py-[12px] text-base',
                        {
                          'rounded-t-2xl': index === 0,
                          'rounded-b-2xl': index === suggestions.length - 1,
                        }
                      )}
                      // Note: This prevents the input from losing focus on selection
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      onSelect={() => handleSuggestionSelect(suggestion)}
                    >
                      {getStyledSuggestion(suggestion.address, value)}
                    </CommandItem>
                  );
                })
              : null}
          </CommandList>
        </Command>
      }
      content={errorText}
    />
  );
}
